import type { SxProps, Theme } from '@mui/material/styles';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useListContext } from 'react-admin';

export type ListFilterSelectChoice = {
    id: string | number;
    name: string;
};

export type ListFilterSelectProps = {
    /** Filter field name (query param key sent with list requests). */
    source: string;
    label: string;
    choices: readonly ListFilterSelectChoice[] | ListFilterSelectChoice[] ;
    /** When true, an extra option clears this filter (removes the key from filter values). */
    allowEmpty?: boolean;
    emptyOptionLabel?: string;
    sx?: SxProps<Theme>;
};

/**
 * Select bound to react-admin list filters via {@link useListContext}.
 * Use inside a {@link List} aside, toolbar filters, or any child of the list view.
 */
export const ListFilterSelect = (props: ListFilterSelectProps) => {
    const { source, label, choices, allowEmpty = true, emptyOptionLabel = 'All', sx } = props;
    const { filterValues, setFilters } = useListContext();
    const allowed = new Set(choices.map((c) => String(c.id)));
    const raw = filterValues[source];
    const value = (raw != null && allowed.has(String(raw))) ? String(raw) : '';

    return (
        <FormControl
            fullWidth
            margin='dense'
            size='small'
            sx={[{ mt: 1 }, ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx])]}
        >
            <InputLabel id={`${source}-filter-label`}>{label}</InputLabel>
            <Select
                labelId={`${source}-filter-label`}
                label={label}
                value={value}
                onChange={(e) => {
                    const v = e.target.value as string;
                    const next: Record<string, unknown> = { ...filterValues };
                    if (allowEmpty && v === '') {
                        delete next[source];
                    } else {
                        next[source] = v;
                    }
                    setFilters(next, undefined, false);
                }}
            >
                {allowEmpty ? <MenuItem value=''>{emptyOptionLabel}</MenuItem> : null}
                {choices
                    .filter((c) => String(c.id) !== '')
                    .map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                            {c.name}
                        </MenuItem>
                    ))}
            </Select>
        </FormControl>
    );
};


export default ListFilterSelect